/** Send-practice-link composer: goals → activities → mint. */
import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import type { Archetype } from '../../lib/practice/rubrics';

interface Props {
  studentId: string;
  sessionId?: string | undefined;
  onClose: () => void;
}

interface Goal {
  id: string;
  area: string;
  goal_text: string;
}

interface Activity {
  id: string;
  goal_id: string | null;
  activity_text: string;
}

interface SessionRow {
  id: string;
}

function activityTitle(text: string): string {
  const firstLine = text.split('\n')[0] ?? 'Untitled activity';
  return firstLine.replace(/^\s*#*\s*/, '').slice(0, 60) || 'Untitled activity';
}

/**
 * Pick the practice archetype that matches a goal area so the
 * #252 multi-archetype player renders the right interactive task.
 * Areas without a dedicated renderer default to picture naming.
 */
function archetypeForGoalArea(area: string): Archetype {
  switch (area) {
    case 'receptive_language':
      return 'receptive_directions';
    case 'expressive_language':
      return 'sentence_completion';
    case 'social':
      return 'conversation_turns';
    case 'cognitive':
      return 'sequencing';
    default:
      return 'picture_naming';
  }
}

export function PracticeLinkComposer({
  studentId,
  sessionId: sessionIdProp,
  onClose,
}: Props): ReactElement {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>([]);
  const [trials, setTrials] = useState(10);
  const [days, setDays] = useState(7);
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(sessionIdProp ?? null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [{ data: goalsData }, { data: activitiesData }] = await Promise.all([
          supabase
            .from('goals')
            .select('id, area, goal_text')
            .eq('student_id', studentId)
            .eq('status', 'active'),
          supabase
            .from('carryover_activities')
            .select('id, goal_id, activity_text')
            .eq('owner_id', (await supabase.auth.getUser()).data.user?.id ?? ''),
        ]);
        if (cancelled) return;
        setGoals((goalsData ?? []) as Goal[]);
        setActivities((activitiesData ?? []) as Activity[]);

        if (!sessionIdProp) {
          const { data: sessionData } = await supabase
            .from('sessions')
            .select('id')
            .eq('student_id', studentId)
            .order('scheduled_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (!cancelled) {
            setSessionId((sessionData as SessionRow | null)?.id ?? null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load composer.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, sessionIdProp]);

  const filteredActivities = useMemo(() => {
    if (selectedGoalIds.length === 0) return activities;
    return activities.filter(
      (a) => a.goal_id && selectedGoalIds.includes(a.goal_id),
    );
  }, [activities, selectedGoalIds]);

  async function submit(): Promise<void> {
    setError(null);
    if (!sessionId) {
      setError('No session selected. Schedule a session first.');
      return;
    }
    if (selectedActivityIds.length === 0) {
      setError('Select at least one activity.');
      return;
    }

    const selectedActivities = activities.filter((a) =>
      selectedActivityIds.includes(a.id),
    );
    const { data, error: invokeError } = await supabase.functions.invoke<{ url: string }>(
      'api-mint-parent-handoff-token',
      {
        body: {
          session_id: sessionId,
          ttl_days: days,
          practice_set_payload: {
            nickname: 'Home practice',
            daily_attempt_cap: trials,
            items: selectedActivities.map((activity, position) => {
              const goal = goals.find((g) => g.id === activity.goal_id);
              return {
                carryover_activity_id: activity.id,
                position,
                target_trials: trials,
                scoring_rubric: {
                  goal_id: activity.goal_id,
                  archetype: archetypeForGoalArea(goal?.area ?? ''),
                  title: activityTitle(activity.activity_text),
                },
              };
            }),
          },
        },
      },
    );
    if (invokeError || !data?.url) {
      setError(invokeError?.message ?? 'Unable to create practice link.');
      return;
    }
    setLink(data.url);
  }

  async function copyLink(): Promise<void> {
    if (!link) return;
    await navigator.clipboard?.writeText(link);
  }

  async function shareLink(): Promise<void> {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Home practice link', url: link });
        return;
      } catch {
        // Fall back to clipboard on share cancellation/failure.
      }
    }
    await copyLink();
  }

  function toggleGoal(goalId: string): void {
    setSelectedGoalIds((prev) => {
      const next = prev.includes(goalId)
        ? prev.filter((id) => id !== goalId)
        : [...prev, goalId];
      // Deselect any activities whose goal was removed.
      setSelectedActivityIds((activitiesPrev) =>
        activitiesPrev.filter((id) => {
          const activity = activities.find((a) => a.id === id);
          return activity?.goal_id ? next.includes(activity.goal_id) : false;
        }),
      );
      return next;
    });
  }

  function toggleActivity(activityId: string): void {
    setSelectedActivityIds((prev) =>
      prev.includes(activityId)
        ? prev.filter((id) => id !== activityId)
        : [...prev, activityId],
    );
  }

  if (loading) {
    return (
      <section className="card stack-md">
        <button type="button" className="link-button" onClick={onClose}>
          ← Back
        </button>
        <p className="tagline">Loading…</p>
      </section>
    );
  }

  return (
    <section className="card stack-md">
      <button type="button" className="link-button" onClick={onClose}>
        ← Back
      </button>
      <h2>Send practice link</h2>
      {error && <p className="error">{error}</p>}

      <fieldset>
        <legend>Goals</legend>
        {goals.length === 0 && (
          <p className="tagline">No active goals. Add a goal first.</p>
        )}
        {goals.map((goal) => (
          <label key={goal.id}>
            <input
              type="checkbox"
              checked={selectedGoalIds.includes(goal.id)}
              onChange={() => toggleGoal(goal.id)}
            />{' '}
            {goal.area}: {goal.goal_text}
          </label>
        ))}
      </fieldset>

      <fieldset>
        <legend>Activities</legend>
        {filteredActivities.length === 0 && (
          <p className="tagline">
            {selectedGoalIds.length === 0
              ? 'Select a goal to see activities.'
              : 'No activities for the selected goals.'}
          </p>
        )}
        {filteredActivities.map((activity) => (
          <label key={activity.id}>
            <input
              type="checkbox"
              checked={selectedActivityIds.includes(activity.id)}
              onChange={() => toggleActivity(activity.id)}
            />{' '}
            {activityTitle(activity.activity_text)}
          </label>
        ))}
      </fieldset>

      <label>
        Target trials
        <input
          type="range"
          min={5}
          max={20}
          value={trials}
          onChange={(e) => setTrials(Number(e.target.value))}
        />
        <span>{trials}</span>
      </label>

      <label>
        Schedule window
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={3}>3 days</option>
          <option value={7}>7 days</option>
          <option value={14}>14 days</option>
        </select>
      </label>

      <button
        type="button"
        className="snap-button"
        disabled={selectedActivityIds.length === 0 || !sessionId}
        onClick={() => void submit()}
      >
        Create link
      </button>

      {link && (
        <div className="card stack-md">
          <input readOnly value={link} />
          <div className="button-row">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void copyLink()}
            >
              Copy link
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void shareLink()}
            >
              Share link
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default PracticeLinkComposer;
